import { Injectable, inject } from '@angular/core';
import { Storage, ref, getDownloadURL } from '@angular/fire/storage';
import JSZip from 'jszip';
import { Order, Photo } from './order.model';

@Injectable({
  providedIn: 'root'
})
export class DownloadService {
  private storage = inject(Storage);

  async downloadOrderPhotos(order: Order): Promise<void> {
    try {
      const zip = new JSZip();
      const photoEntries = Object.entries(order.photos);

      console.log(`Starting download of ${photoEntries.length} photos for order ${order.id}`);

      // Create the root folder name with user name and size
      const rootFolderName = `${order.userName}_${order.size.width}x${order.size.height}`;

      // Process photos concurrently with Promise.all
      await Promise.all(photoEntries.map(([key, photo]) => this.addPhotoToZip(zip, photo, key, rootFolderName)));

      console.log('All photos added to zip, generating archive...');

      // Generate and download the zip file
      const content = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });

      console.log(`Zip file generated, size: ${content.size} bytes`);
      
      // Create filename with user name and size (same as root folder)
      const filename = `${rootFolderName}.zip`;
      this.downloadBlob(content, filename);

    } catch (error) {
      console.error('Error downloading photos:', error);
      alert('Failed to download photos. Please try again.');
    }
  }

  private async addPhotoToZip(zip: JSZip, photo: Photo, key: string, rootFolderName: string): Promise<void> {
    try {
      console.log(`Downloading photo: ${key} from ${photo.blobName}`);
      
      const photoRef = ref(this.storage, photo.blobName);
      const downloadURL = await getDownloadURL(photoRef);
      console.log(`Got download URL for ${key}: ${downloadURL}`);
      
      const response = await fetch(downloadURL);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      console.log(`Downloaded blob for ${key}, size: ${blob.size} bytes, type: ${blob.type}`);
      
      if (blob.size === 0) {
        throw new Error('Downloaded blob is empty');
      }

      // Keep original filename from blobName without any modifications
      const originalFilename = photo.blobName.split('/').pop() || `${key}_photo`;
      
      // Root folder is now rootFolderName, then subfolder is numberOfPrints (default 1)
      const folderName = String(photo.numberOfPrints ?? 1);
      const zipPath = `${rootFolderName}/${folderName}/${originalFilename}`;

      // Add the original blob to zip without any processing
      zip.file(zipPath, blob);
      console.log(`Added ${zipPath} to zip`);
      
    } catch (error) {
      console.error(`Failed to download photo ${key}:`, error);
      // Don't throw here, just log and continue with other photos
    }
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
